import React, { useEffect, useState, useCallback, useMemo, forwardRef } from "react";
import clsx from "clsx";
import { Dialog, DialogContent, Grid } from "@mui/material";
import { Icons, Button, Collapse, IconButton, List, ListItemButton, ListItemText, TextField, Typography, Alert, Box } from "@curves/ui-base";
import { isEmpty, isFunction } from "@util/utils";
import { ProductBox } from "@icoach/accountBook/components";
import useProductApi from "@apis/useProductApi";
import Logo from "@images/nav_logo.svg";
import { useProductSearch } from "../hooks/useProductSearch";
import { useProductData } from "../hooks/useProductData";
import { useOrderCart } from "../hooks/useOrderCart";
import { isAllMerchandiseMenu, isProductItem } from "@icoach/accountBook/createOrder/CreateOrderPage.util";

/**
 * 商品分類選單組件（遞迴）
 * 支持多層級分類選單
 */
const ShoppingMenu = ({ className, isOpen = false, currentMenu, menuData: menuDataProps = [], layer = 0, setCurrentMenu, productTypeText, onClick: onClickProps }) => {
	// 過濾被禁用的選單項
	const menuData = useMemo(() => {
		return Array.isArray(menuDataProps) ? menuDataProps.filter(item => !item.disabled) : [];
		// eslint-disable-next-line
	}, [menuDataProps]);

	// 初始化子選單的開合狀態 (每個選單項一個布林值)
	const [openSubmenu, setOpenSubmenu] = useState(() => menuData.map(() => false));

	// 使用 useEffect 監聽 menuData 變化，以同步 openSubmenu 陣列長度
	useEffect(
		() => {
			// 當 menuData 內容變化時 (例如，props 傳入的選單不同了)
			if (menuData.length !== openSubmenu.length) {
				setOpenSubmenu(menuData.map(() => false));
			}
		},
		// eslint-disable-next-line
		[menuData, openSubmenu.length],
	);

	const handleToggleSubmenu = useCallback(
		index => {
			setOpenSubmenu(prev => {
				let _arr = [...prev];
				_arr[index] = !_arr[index];
				return _arr;
			});
		},
		// eslint-disable-next-line
		[],
	);

	const handleClick = useCallback(
		item => {
			// 確保 productTypeText 傳遞的是當前層級的類型或前一層的類型
			const _productTypeText = productTypeText || item.text;
			setCurrentMenu(item);
			if (typeof onClickProps === "function") onClickProps(item, _productTypeText);
		},
		// eslint-disable-next-line
		[productTypeText, setCurrentMenu, onClickProps],
	);

	// 自動選中第一個分類 (僅在頂層選單且未選中時觸發)
	// 使用 useEffect 避免阻塞視覺更新，且只在組件 mount 時執行一次
	useEffect(
		() => {
			// 只有在頂層 (layer === 0) 且當前沒有選中菜單時才執行自動選中
			if (layer === 0 && !currentMenu && menuData.length > 0) {
				let item = menuData[0];
				if (item) {
					if (!isEmpty(item.children)) {
						// 如果是父分類，則自動展開第一個子選單
						handleToggleSubmenu(0);
					} else {
						// 如果是葉子節點，則自動點擊選中
						handleClick(item);
					}
				}
			}
		},
		// eslint-disable-next-line
		[currentMenu, menuData, layer, handleClick, handleToggleSubmenu],
	);

	// 如果沒有任何菜單資料，則不渲染
	if (menuData.length === 0) return null;

	return (
		<Collapse in={isOpen}>
			<List className={className}>
				{Array.isArray(menuData) &&
					menuData.map((item, i) => {
						// 檢查 item.children 是否有可用的子選單
						const hasChildren = !isEmpty(item.children) && item.children.some(child => !child.disabled);

						// 如果選單項沒有內容，且沒有子選單，則跳過渲染
						if (isEmpty(item.text) && !hasChildren) return null;

						return (
							<React.Fragment key={`${item.text}_${i}`}>
								<ListItemButton
									className={clsx({
										"is-current": currentMenu?.text === item.text && !hasChildren, // 只有葉子節點才標記為 "is-current"
									})}
									onClick={() => {
										if (hasChildren) {
											handleToggleSubmenu(i);
										} else {
											handleClick(item);
										}
									}}
								>
									<ListItemText primary={item.text} />
									{hasChildren && (openSubmenu[i] ? <Icons.AngleUp /> : <Icons.AngleDown />)}
								</ListItemButton>

								{/* 遞迴呼叫自身 */}
								{hasChildren && (
									<ShoppingMenu
										menuData={item.children}
										isOpen={openSubmenu[i]}
										layer={layer + 1}
										currentMenu={currentMenu}
										// productTypeText: 如果是頂層，則為當前分類名稱；否則繼承父層
										productTypeText={layer === 0 ? item.text : productTypeText}
										setCurrentMenu={setCurrentMenu}
										onClick={onClickProps}
									/>
								)}
							</React.Fragment>
						);
					})}
			</List>
		</Collapse>
	);
};

/**
 * 商品項目組件（統一處理產品和費用）
 * 根據是否有 productID 判斷類型
 */
const ShoppingMenuItem = ({ sourceData = {}, productTypeText, isProductInCart, isFeeInCart, addItem, removeItem, onCartChange = null }) => {
	// 檢查類型（使用共享的工具函數保持一致性）
	const isProduct = isProductItem(sourceData);

	// 商品數據結構
	const { productID, variantID, pictureSrc, productName, variantName, sku, stock, isLimited, price } = sourceData || {};
	// 費用數據結構
	const { text } = sourceData || {};

	// --- 從購物車 Hook 即時計算選中狀態 ---
	const isSelect = useMemo(() => {
		if (isProduct) {
			return isProductInCart(productID, variantID);
		} else {
			return isFeeInCart(text);
		}
		// eslint-disable-next-line
	}, [isProduct, isProductInCart, isFeeInCart, productID, variantID, text]);

	const handleClickItem = useCallback(() => {
		if (isSelect) {
			// 如果已選中，則刪除
			removeItem(sourceData);
		} else {
			// 如果未選中，則新增
			const itemToAdd = isProduct ? sourceData : { ...sourceData, productTypeText };
			addItem(itemToAdd);
		}

		// 通知父組件購物車已變更
		if (onCartChange) {
			onCartChange();
		}
		// eslint-disable-next-line
	}, [isSelect, isProduct, sourceData, productTypeText, addItem, removeItem, onCartChange]);

	// --- 渲染 ---
	if (isProduct) {
		return (
			<ProductBox
				className={clsx({ "is-select": isSelect })}
				name={productName}
				imgUrl={pictureSrc}
				variantName={variantName}
				sku={sku}
				price={price}
				stock={stock}
				isLimited={isLimited}
				onClick={handleClickItem}
			/>
		);
	} else {
		return <ProductBox className={clsx({ "is-select": isSelect }, "fee-item")} name={text} onClick={handleClickItem} />;
	}
};

/**
 * 用於新增收入頁面，提供商品選擇功能
 */
const ProductSelectionDialog = forwardRef(({ className, open, sourceData, onClose, refresh: onCartUpdate }, ref) => {
	if (!open) return null;
	const { menuData, initialCartItems = [] } = sourceData || {};

	const { getProductSaleOptionsApi, getAllMerchandiseDataApi } = useProductApi();

	// 當前選中的菜單項
	const [currentMenu, setCurrentMenu] = useState(null);

	// 使用 useOrderCart Hook 管理對話框內的購物車
	const { cartItems, itemCount, addItem, removeItem, isProductInCart, isFeeInCart, setCart } = useOrderCart([]);

	// 1. 搜尋邏輯 Hook
	const { searchKeyword, searchKey, handleSearchChange, handleComposition, resetSearch, searchAllMerchandiseWithDebounce } = useProductSearch(getAllMerchandiseDataApi, currentMenu);

	// 2. 數據邏輯 Hook
	const { productTypeTextRef, filterProducts, handleClickMenu } = useProductData(getProductSaleOptionsApi, searchKeyword, currentMenu, setCurrentMenu, resetSearch, searchAllMerchandiseWithDebounce);

	// 確認結帳
	const handleClickCheckout = useCallback(
		() => {
			isFunction(onCartUpdate) && onCartUpdate(cartItems);
			isFunction(onClose) && onClose();
		},
		// eslint-disable-next-line
		[cartItems, onCartUpdate, onClose],
	);

	// 當對話框打開時，從 initialCartData 初始化購物車
	useEffect(() => {
		if (open) {
			setCart(initialCartItems || []);
		}
		// eslint-disable-next-line
	}, [open]);

	return (
		<Dialog className={clsx(className, "shopping-menu-dialog", "simple-content", "dialog")} PaperProps={{ className: "wx-90rem" }} open={open} fullWidth>
			{onClose && (
				<Box className="dialog-close-btn">
					<IconButton size="large" onClick={onClose}>
						<Icons.Close htmlColor="#fff" />
					</IconButton>
				</Box>
			)}
			<DialogContent className={"shopping-menu-container"}>
				<Box className={"shopping-menu-side-bar"}>
					<Box className={"logo"}>
						<img src={Logo} alt="Curves" />
					</Box>
					<ShoppingMenu currentMenu={currentMenu} className={"menu"} menuData={menuData} setCurrentMenu={setCurrentMenu} onClick={handleClickMenu} isOpen />
				</Box>

				{/* 右側內容：搜尋與商品列表 */}
				<Box className={"shopping-menu-content"}>
					<Box className={"products"}>
						{/* 搜尋欄位 */}
						<Grid className={"mb-4"} spacing={2} container>
							<Grid xs={6} item>
								<TextField
									key={searchKey}
									InputProps={{ placeholder: "搜尋品項名稱或商品條碼" }}
									onCompositionStart={handleComposition}
									onCompositionUpdate={handleComposition}
									onCompositionEnd={handleComposition}
									onChange={handleSearchChange}
									fullWidth
								/>
							</Grid>
						</Grid>

						{/* 提示訊息：僅在「全部商品」且無結果時顯示 */}
						{isAllMerchandiseMenu(currentMenu) && filterProducts.length === 0 && (
							<Grid container spacing={2}>
								<Grid xs={12} item>
									<Alert variant="filled" severity="info" color="secondary">
										請輸入品項名稱或商品條碼
									</Alert>
								</Grid>
							</Grid>
						)}

						{/* 商品/費用列表 */}
						<Grid container spacing={3}>
							{filterProducts.map(item => {
								// 使用共享的工具函數判斷類型，保持一致性
								const isProduct = isProductItem(item);
								const key = isProduct ? `product-${item.productID}-${item.variantID}` : `fee-${item.text}`;
								return (
									<Grid key={key} xs={12} item>
										<ShoppingMenuItem
											sourceData={item}
											productTypeText={productTypeTextRef.current}
											isProductInCart={isProductInCart}
											isFeeInCart={isFeeInCart}
											addItem={addItem}
											removeItem={removeItem}
										/>
									</Grid>
								);
							})}
						</Grid>
					</Box>

					{/* 底部：購物車數量和結帳按鈕 */}
					<Box className={"shopping-menu-footer"}>
						<Typography variant={"h6"}>
							已經選取：
							<strong className={"text-primary mr-1"}>{itemCount}</strong>
							個品項
						</Typography>
						<Box className={"btn-group"}>
							<Button className={"btn-checkout"} color={"warning"} variant={"contained"} size={"large"} onClick={handleClickCheckout}>
								進行結帳
							</Button>
						</Box>
					</Box>
				</Box>
			</DialogContent>
		</Dialog>
	);
});

ProductSelectionDialog.displayName = "ProductSelectionDialog";

export default ProductSelectionDialog;
